import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { AppointmentRecord } from "~/api/API";
import { resolveFileOwnership } from "~/api/queries/resolveFileOwnership";
import {
  appointmentFilesKeys,
  FileDisplayData,
} from "~/api/queries/useAppointmentFiles";
import { appointmentFilesBatchQueryConfig } from "~/api/queries/useAppointmentFilesBatch";
import { useLinkedAppointmentFiles } from "~/api/queries/useLinkedAppointmentFiles";
import { FEATURE_FLAGS } from "~/services/featureFlags/constants";
import { useFeatureFlag } from "~/services/featureFlags/provider";

interface UseResolvedLinkedFilesOptions {
  /**
   * Transform/filter the resolved files for this consumer (e.g. exclude billing
   * receipts). Applied after ownership resolution, mirroring the `select` option
   * on useAppointmentFiles.
   */
  select?: (files: FileDisplayData[]) => FileDisplayData[];
}

interface UseResolvedLinkedFilesResult {
  /** Deduplicated files across the linked family, with ownership-based isReadOnly */
  files: FileDisplayData[];
  /** Whether data is still loading */
  isLoading: boolean;
  /** Error if any query failed */
  error: Error | null;
  /** Refetch files for every appointment in the linked family */
  refetch: () => Promise<void>;
}

const EMPTY_FILES: FileDisplayData[] = [];

/**
 * Whether the Wave 3 readers (`useResolvedLinkedFiles` and the Case Documents
 * sidebar) read the server-resolved family view. Readers start no file read
 * while `loading`, so a page doesn't fire the per-member junction reads and
 * then replace them.
 */
export function useLinkedFileReads(): { isEnabled: boolean; loading: boolean } {
  const { isEnabled, loading } = useFeatureFlag(
    FEATURE_FLAGS.USE_ECS_FILE_READS_LINKED,
  );
  return { isEnabled, loading };
}

/**
 * Reads files for an appointment's full reschedule/linked family and resolves
 * ownership relative to it, returning a deduplicated flat list whose
 * `isReadOnly` / `referenceStatus` / `appointmentID` are attributed relative to
 * the current appointment.
 *
 * `use-ecs-file-reads-linked` on (Wave 3 of
 * `specs/appointment-file-reads-portal-cutover.md`) → one api-server read: the
 * server applies the same rules as `resolveFileOwnership`
 * (`resolve_family_files`). Off → the originalAppointmentID GSI, one read per
 * family member, then `resolveFileOwnership` here.
 *
 * Pass `undefined` to disable (no fetch) — lets a caller gate this on a flag
 * without breaking the Rules of Hooks.
 *
 * With the flag off, the read path inside `useLinkedAppointmentFiles` is itself
 * flag-selected (junction vs `UploadedFile.bySourceAppointment` GSI) by
 * `PORTAL_READ_FILES_BY_SOURCE_APPOINTMENT`; this hook is agnostic to which.
 */
export function useResolvedLinkedFiles(
  appointment: AppointmentRecord | null | undefined,
  options: UseResolvedLinkedFilesOptions = {},
): UseResolvedLinkedFilesResult {
  const { select } = options;
  const queryClient = useQueryClient();

  const { isEnabled, loading: flagLoading } = useLinkedFileReads();
  const readViaApi = !flagLoading && isEnabled;
  const readLegacy = !flagLoading && !isEnabled;

  // Flag off: per-member reads + client-side resolve.
  const { filesByAppointment, appointmentMap, isLoading, error } =
    useLinkedAppointmentFiles(readLegacy ? appointment : undefined);

  // Flag on: the server-resolved view.
  const appointmentId = appointment?.id ?? "";
  const viaApi = useQuery({
    ...appointmentFilesBatchQueryConfig(appointmentId),
    select,
    enabled: readViaApi && appointmentId !== "",
  });

  const legacyFiles = useMemo(() => {
    if (!appointment || isLoading || filesByAppointment.size === 0) {
      return EMPTY_FILES;
    }

    const resolved = resolveFileOwnership({
      filesByAppointment,
      appointmentMap,
      currentAppointmentId: appointment.id,
    });

    return select ? select(resolved) : resolved;
  }, [appointment, isLoading, filesByAppointment, appointmentMap, select]);

  if (flagLoading) {
    return {
      files: EMPTY_FILES,
      isLoading: !!appointment,
      error: null,
      refetch: async () => {
        // Nothing read yet: the first read starts once the flag is known.
      },
    };
  }

  if (readViaApi) {
    return {
      files: viaApi.data ?? EMPTY_FILES,
      isLoading: viaApi.isLoading,
      error: viaApi.error,
      refetch: async () => {
        await queryClient.invalidateQueries({
          queryKey: appointmentFilesKeys.api(appointmentId),
        });
      },
    };
  }

  return {
    files: legacyFiles,
    isLoading,
    error,
    refetch: async () => {
      const invalidations = Array.from(appointmentMap.keys()).map((id) =>
        queryClient.invalidateQueries({
          queryKey: appointmentFilesKeys.detail(id),
        }),
      );
      await Promise.all(invalidations);
    },
  };
}
